import { faBarcodeScan, faUser } from "@fortawesome/pro-solid-svg-icons";

import { type UserIdentifier } from "~/providers/store/employee";
import { logger } from "~/services/logger";
import { useScanner } from "~/hooks/use-scanner/use-scanner";
import { CODE_FORMATS } from "~/hooks/use-scanner/code-formats";
import { toast } from "~/components/hooks/use-toast";
import { T } from "@repo/transifex";
import { DisplayErrorMessage } from "~/components/display-error-message";

type ScannerListenerProps = {
  enabled: boolean;
  onIdentifyProductOrUser: (barcode: string) => void;
  onIdentifyUser: (userIdentifier: UserIdentifier) => void;
  isUserAuthenticated: boolean;
};
export const ScannerListener = ({
  enabled,
  onIdentifyProductOrUser,
  onIdentifyUser,
  isUserAuthenticated
}: ScannerListenerProps) => {
  useScanner({
    onSuccess: (result) => {
      switch (result.format) {
        case CODE_FORMATS.QR_CHILD_ID: {
          if (isUserAuthenticated) {
            toast({
              title: <T _str="Session active" />,
              description: (
                <T _str="Complete or cancel the current session to sign in as a new user." />
              ),
              variant: "default",
              icon: faUser
            });
            break;
          }

          onIdentifyUser({ type: "child-id", value: result.value });
          break;
        }

        case CODE_FORMATS.GENERIC: {
          if (isUserAuthenticated) {
            toast({
              title: <T _str="Session active" />,
              description: (
                <T _str="Complete or cancel the current session to sign in as a new user." />
              ),
              variant: "default",
              icon: faUser
            });
            break;
          }

          onIdentifyUser({ type: "child-card-hex", value: result.value });
          break;
        }

        case CODE_FORMATS.BARCODE_PRODUCT_OR_GENERIC: {
          onIdentifyProductOrUser(result.value);
          break;
        }

        default:
          scanToastError(null);

          logger.warn("Unknown scanned code type could not be handled", {
            scan_code_type: result.format,
            scan_code_value: result.value
          });
      }
    },
    onError: (error) => {
      scanToastError(error);
    },
    enabled
  });

  return null;
};

export function scanToastError(
  error: unknown,
  description: React.ReactNode = (
    <DisplayErrorMessage
      error={error}
      fallback={<T _str="Invalid code scanned" />}
    />
  )
) {
  return toast({
    title: <T _str="Scanning failed" />,
    description,
    variant: "destructive",
    icon: faBarcodeScan
  });
}
